import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, CalendarClock, CheckCircle2, ArrowRightLeft, Check, ChevronsUpDown } from 'lucide-react';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

import { delegationService as defaultDelegationService, isDelegationApiError, extractDelegationApiError } from '../services/delegationService';
import type { DelegationService } from '../services/delegationService';
import { DEFAULT_DELEGATION_SETTINGS } from '../types/delegation.types';
import type {
  ApprovalDelegation,
  DelegationApiError,
  DelegationSettings,
  DelegationValidationResult,
  EligibleDelegate,
} from '../types/delegation.types';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useDelegationPermission } from './DelegationPermissionGate';
import { DelegationValidationError } from './DelegationValidationError';

// ---------------------------------------------------------------------------
// Client-side validation
// ---------------------------------------------------------------------------

function validateForm(
  delegateId: string,
  startDate: string,
  endDate: string,
  notes: string,
  settings: DelegationSettings,
  currentUserId?: string
): DelegationValidationResult {
  const errors: DelegationValidationResult['errors'] = [];

  if (!delegateId) {
    errors.push({ field: 'delegateId', code: 'delegate_not_eligible', message: 'Please select a delegate.' });
  }

  if (currentUserId && delegateId === currentUserId) {
    errors.push({ field: 'delegateId', code: 'self_delegation', message: 'You cannot delegate to yourself.' });
  }

  if (!startDate) {
    errors.push({ field: 'startDate', code: 'invalid_date_range', message: 'Start date is required.' });
  }

  if (!endDate) {
    errors.push({ field: 'endDate', code: 'invalid_date_range', message: 'End date is required.' });
  }

  if (startDate && endDate) {
    if (endDate <= startDate) {
      errors.push({ field: 'endDate', code: 'invalid_date_range', message: 'End date must be after start date.' });
    } else {
      const days = differenceInCalendarDays(parseISO(endDate), parseISO(startDate));
      if (days > settings.maxDelegationDays) {
        errors.push({
          field: 'endDate',
          code: 'exceeds_max_duration',
          message: `Delegation exceeds the maximum allowed duration of ${settings.maxDelegationDays} days.`,
        });
      }
    }
  }

  if (settings.requireNotes && !notes.trim()) {
    errors.push({ field: 'notes', code: 'delegate_not_eligible', message: 'Notes are required by your organisation settings.' });
  }

  return { valid: errors.length === 0, errors };
}

function fieldError(
  validation: DelegationValidationResult | null,
  field: DelegationValidationResult['errors'][number]['field']
): string | undefined {
  return validation?.errors.find((e) => e.field === field)?.message;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CreateDelegationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (delegation: ApprovalDelegation) => void;
  delegationService?: DelegationService;
}

type Step = 'form' | 'confirm';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CreateDelegationDialog: React.FC<CreateDelegationDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  delegationService = defaultDelegationService,
}) => {
  const [delegateId, setDelegateId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');

  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<DelegationValidationResult | null>(null);
  const [apiError, setApiError] = useState<DelegationApiError | null>(null);
  const [settings, setSettings] = useState<DelegationSettings>(DEFAULT_DELEGATION_SETTINGS);
  const [createdDelegation, setCreatedDelegation] = useState<ApprovalDelegation | null>(null);

  // Reassign pending timesheets state (PRD §4 Feature B)
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [reassignChecked, setReassignChecked] = useState(false);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignDone, setReassignDone] = useState<number | null>(null); // count when done

  // Eligible delegates loaded from service (mock or live)
  const [eligibleDelegates, setEligibleDelegates] = useState<EligibleDelegate[]>([]);
  const [delegatesLoading, setDelegatesLoading] = useState(false);

  const { canDelegate } = useDelegationPermission();
  const { currentEmployee, timesheets, updateTimesheet } = useTimesheet();
  const { getScreenMode } = useDataSource();
  const currentUserId = currentEmployee?.id != null ? String(currentEmployee.id) : undefined;

  // Filter out the current user from delegate options
  const filteredDelegates = useMemo(
    () => eligibleDelegates.filter((d) => d.id !== currentUserId),
    [eligibleDelegates, currentUserId]
  );

  const [delegatePickerOpen, setDelegatePickerOpen] = useState(false);

  // Load settings and eligible delegates on open
  useEffect(() => {
    if (open) {
      delegationService.getDelegationSettings()
        .then(setSettings)
        .catch(() => { /* use defaults */ });

      setDelegatesLoading(true);
      delegationService.getEligibleDelegates()
        .then(setEligibleDelegates)
        .catch(() => { /* keep empty list */ })
        .finally(() => setDelegatesLoading(false));
    }
  }, [open, delegationService]);

  // Reset on close
  function reset() {
    setDelegateId('');
    setStartDate('');
    setEndDate('');
    setNotes('');
    setStep('form');
    setLoading(false);
    setValidation(null);
    setApiError(null);
    setCreatedDelegation(null);
    setPendingCount(null);
    setReassignChecked(false);
    setReassignLoading(false);
    setReassignDone(null);
  }

  function handleOpenChange(next: boolean) {
    if (!loading) {
      if (!next) reset();
      onOpenChange(next);
    }
  }

  // Derived
  const durationDays = startDate && endDate
    ? differenceInCalendarDays(parseISO(endDate), parseISO(startDate))
    : 0;

  // Submit
  async function handleSubmit() {
    setApiError(null);
    const v = validateForm(delegateId, startDate, endDate, notes, settings, currentUserId);
    setValidation(v);
    if (!v.valid) return;

    try {
      setLoading(true);
      const [delegation, countResponse] = await Promise.all([
        delegationService.createDelegation({
          delegateId,
          startDate,
          endDate,
          notes: notes.trim() || undefined,
        }),
        delegationService.getPendingTimesheetCount(),
      ]);
      setCreatedDelegation(delegation);
      setPendingCount(countResponse.pendingCount);
      setStep('confirm');
    } catch (err) {
      const delegationError = extractDelegationApiError(err);
      if (delegationError) {
        setApiError(delegationError);
      } else if (isDelegationApiError(err)) {
        setApiError(err);
      } else {
        setApiError({ error: 'forbidden', message: 'Something went wrong. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleReassign() {
    if (!createdDelegation) return;
    try {
      setReassignLoading(true);
      const result = await delegationService.reassignPendingTimesheets(createdDelegation.id);
      setReassignDone(result.reassignedCount);

      // In demo mode, update timesheet context to reflect reassignment
      const screenMode = getScreenMode('approvals');
      if (screenMode !== 'live' && currentEmployee) {
        const delegateId = parseInt(createdDelegation.delegateId, 10);
        const pendingForManager = timesheets.filter(
          (ts) => ts.status === 'submitted' && ts.approverId === currentEmployee.id
        );
        for (const ts of pendingForManager) {
          updateTimesheet({
            ...ts,
            delegatedApproverId: currentEmployee.id,
            delegatedApproverName: currentEmployee.name,
            approverId: delegateId,
            approverName: createdDelegation.delegateName,
          });
        }
      }
    } catch {
      // Swallow — reassign failure is non-blocking; delegation already created
    } finally {
      setReassignLoading(false);
    }
  }

  function handleDone() {
    if (createdDelegation) onSuccess(createdDelegation);
    reset();
  }

  // ---------------------------------------------------------------------------
  // Confirmation step
  // ---------------------------------------------------------------------------

  if (step === 'confirm' && createdDelegation) {
    const showReassignPrompt = pendingCount !== null && pendingCount > 0 && reassignDone === null;

    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="sm:max-w-md flex flex-col">
          <SheetHeader>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <SheetTitle>Delegation Created</SheetTitle>
            </div>
            <SheetDescription>
              {`${createdDelegation.delegateName} can now approve timesheets on your behalf.`}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto py-4 px-4 space-y-4">
            {/* Delegation summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delegate</span>
                <span className="font-medium">{createdDelegation.delegateName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Period</span>
                <span className="font-medium">
                  {format(parseISO(createdDelegation.startDate), 'd MMM')} –{' '}
                  {format(parseISO(createdDelegation.endDate), 'd MMM yyyy')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration</span>
                <span className="font-medium">{createdDelegation.durationDays} day{createdDelegation.durationDays !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="text-green-600 font-medium">Active</span>
              </div>
              {createdDelegation.notes && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground shrink-0">Notes</span>
                  <span className="font-medium text-right">{createdDelegation.notes}</span>
                </div>
              )}
            </div>

            {/* Reassign pending timesheets prompt — PRD §4 Feature B */}
            {/* Only shown when pendingCount > 0 and reassign not yet actioned */}
            {showReassignPrompt && (
              <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <ArrowRightLeft className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium leading-snug">
                      Reassign pending timesheets?
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      You have {pendingCount} pending timesheet{pendingCount !== 1 ? 's' : ''} in your
                      approvals queue. Would you like to reassign{' '}
                      {pendingCount !== 1 ? 'them' : 'it'} to{' '}
                      <span className="font-medium text-foreground">{createdDelegation.delegateName}</span> now?
                      This action cannot be undone.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 pl-1">
                  <Checkbox
                    id="reassign-checkbox"
                    checked={reassignChecked}
                    onCheckedChange={(checked) => setReassignChecked(checked === true)}
                    disabled={reassignLoading}
                  />
                  <label
                    htmlFor="reassign-checkbox"
                    className="text-sm cursor-pointer select-none"
                  >
                    Yes, reassign {pendingCount} pending timesheet{pendingCount !== 1 ? 's' : ''} to{' '}
                    {createdDelegation.delegateName}
                  </label>
                </div>

                {reassignChecked && (
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={handleReassign}
                    disabled={reassignLoading}
                  >
                    {reassignLoading && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                    {reassignLoading
                      ? 'Reassigning…'
                      : `Reassign ${pendingCount} timesheet${pendingCount !== 1 ? 's' : ''}`}
                  </Button>
                )}
              </div>
            )}

            {/* Reassign success confirmation */}
            {reassignDone !== null && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-start gap-3">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <p className="text-sm text-green-800">
                  <span className="font-medium">
                    {reassignDone} timesheet{reassignDone !== 1 ? 's' : ''} reassigned.
                  </span>{' '}
                  {createdDelegation.delegateName} will see{' '}
                  {reassignDone !== 1 ? 'them' : 'it'} in their approvals queue.
                </p>
              </div>
            )}
          </div>

          <SheetFooter>
            <Button
              onClick={handleDone}
              className="w-full"
              disabled={reassignLoading}
            >
              Done
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  // ---------------------------------------------------------------------------
  // Form step
  // ---------------------------------------------------------------------------

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="sm:max-w-md flex flex-col">
        <SheetHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <CalendarClock className="h-4 w-4 text-primary" />
            </div>
            <SheetTitle>Set Up Delegation</SheetTitle>
          </div>
          <SheetDescription>
            Choose who will approve timesheets on your behalf and for how long.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 px-4 space-y-5">

          {/* Delegate picker */}
          <div className="space-y-1.5">
            <Label htmlFor="delegate-select">
              Delegate <span className="text-destructive">*</span>
            </Label>
            <Popover open={delegatePickerOpen} onOpenChange={setDelegatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="delegate-select"
                  variant="outline"
                  role="combobox"
                  aria-expanded={delegatePickerOpen}
                  className={cn(
                    'w-full justify-between font-normal',
                    fieldError(validation, 'delegateId') ? 'border-destructive' : '',
                    !delegateId && 'text-muted-foreground'
                  )}
                  disabled={loading || delegatesLoading}
                >
                  {delegatesLoading
                    ? 'Loading employees…'
                    : delegateId
                      ? filteredDelegates.find((d) => d.id === delegateId)?.name ?? 'Select a colleague…'
                      : 'Select a colleague…'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0"
                style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '300px' }}
                align="start"
              >
                <Command>
                  <CommandInput placeholder="Search delegates..." />
                  <CommandList>
                    <CommandEmpty>No delegate found.</CommandEmpty>
                    <CommandGroup>
                      {filteredDelegates.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={user.name}
                          onSelect={() => {
                            setDelegateId(user.id);
                            setDelegatePickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              delegateId === user.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          {user.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {fieldError(validation, 'delegateId') && (
              <p className="text-xs text-destructive">{fieldError(validation, 'delegateId')}</p>
            )}
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">
                Start Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={loading}
                className={fieldError(validation, 'startDate') ? 'border-destructive' : ''}
              />
              {fieldError(validation, 'startDate') && (
                <p className="text-xs text-destructive">{fieldError(validation, 'startDate')}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">
                End Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                disabled={loading}
                className={fieldError(validation, 'endDate') ? 'border-destructive' : ''}
              />
              {fieldError(validation, 'endDate') && (
                <p className="text-xs text-destructive">{fieldError(validation, 'endDate')}</p>
              )}
            </div>
          </div>

          {/* Duration hint */}
          {durationDays > 0 && (
            <p className="text-xs text-muted-foreground -mt-2">
              Duration: {durationDays} day{durationDays !== 1 ? 's' : ''}
              {durationDays > settings.maxDelegationDays && (
                <span className="text-destructive ml-1">
                  (exceeds maximum of {settings.maxDelegationDays} days)
                </span>
              )}
            </p>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="notes">
              Notes{' '}
              <span className="text-muted-foreground font-normal text-xs">
                {settings.requireNotes ? '(required)' : '(optional)'}
              </span>
            </Label>
            <Textarea
              id="notes"
              placeholder="Any instructions for your delegate…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              disabled={loading}
              className={`resize-none ${fieldError(validation, 'notes') ? 'border-destructive' : ''}`}
            />
            {fieldError(validation, 'notes') && (
              <p className="text-xs text-destructive">{fieldError(validation, 'notes')}</p>
            )}
          </div>

          {/* Permission error */}
          {!canDelegate && (
            <DelegationValidationError message="You don't have permission to create delegations. Contact your administrator." />
          )}

          {/* API error */}
          <DelegationValidationError apiError={apiError} />

        </div>

        <SheetFooter className="gap-2 flex-row">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleSubmit}
            disabled={loading || !canDelegate}
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {loading ? 'Creating…' : 'Create Delegation'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
